import React, {useEffect} from "react"
import {Meta, Resource} from "../../resources/common"
import {Customer} from "../../resources/customer"
import {useIsOrgUser} from "../../services/auth"
import {AllowedCustomersColumns, CustomersColumns} from "./Customer.types"
import {DataTable, DataTableBody, DataTableCell, DataTableHead, DataTableRow} from "../DataTable/DataTable"
import {isProdEnv} from "../../utilities/environment"
import ReactTooltip from "react-tooltip"
import Icon from "../Icon/Icon"
import CustomerRow from "./CustomerRow"
import PagingNavBar from "../PagingNavBar/PagingNavBar"

interface CustomersTableProps {
	customers: Array<Customer>
	hasResults: boolean
	hasPrev: boolean
	hasNext: boolean
	prev: () => void
	next: () => void
	isUsingPaging: boolean
	include?: Resource[]
	includedColumns: Array<AllowedCustomersColumns>
	meta?: Meta
	isFiltering?: boolean
	fullHeight?: boolean
	sortFunction: () => void
	sortBy: string
	onSelect?: (customer: Customer) => void
}

export default function CustomersTable({
	customers,
	includedColumns,
	hasPrev,
	hasNext,
	prev,
	next,
	hasResults,
	isUsingPaging,
	include,
	meta,
	isFiltering,
	fullHeight,
	sortFunction,
	sortBy,
	onSelect,
}: CustomersTableProps) {
	const isOrgUser = useIsOrgUser()
	const tutorialCallButton = document.querySelector(".tutorial-call-button")
	const noContent = customers.length === 0

	useEffect(() => {
		if (!noContent && tutorialCallButton) {
			tutorialCallButton.classList.add("tutorial-call-button-with-animation")
		}
	}, [])

	return (
		<div className={"customers-table"}>
			<DataTable
				isEmpty={noContent}
				fullHeight={fullHeight}
				stickyAction={fullHeight}
				noContentText={
					<>
						{!isFiltering ? (
							<>
								No{" "}
								<a className="doc-link" href="https://docs.unit.co/#applications" target="_blank" rel="noreferrer">
									customers
								</a>{" "}
								yet <br /> <br />
								<span className={"no-context-explanation"}>
									{" "}
									Once applications are created and approved, customers will automatically be created{" "}
								</span>
							</>
						) : (
							"No customers found"
						)}
					</>
				}
				noContentCtaButton={
					!isProdEnv() &&
					!isFiltering &&
					isOrgUser && (
						<button
							className="tutorial-cta-button"
							data-tracking-label="tutorial-customers-cta"
							onClick={(e) => {
								const target = e.target as HTMLButtonElement
								target.classList.add("tutorial-cta-button-was-used")
								if (tutorialCallButton) {
									ReactTooltip.show(tutorialCallButton)
								}
							}}
						>
							<Icon icon={"programming-code--programing-apps-websites"} color="white" />
							Test Create Customer API
						</button>
					)
				}
			>
				<DataTableHead>
					<DataTableRow>
						{Object.entries(includedColumns).map((column) => {
							if (column[1] === CustomersColumns.createdAt) {
								return (
									<DataTableCell
										clickable
										onClick={() => sortFunction()}
										key={column[1]}
										sortable
										sortApplied={sortBy === "createdAt"}
									>
										{column[1]}
									</DataTableCell>
								)
							}
							return <DataTableCell key={column[0]}>{column[1]}</DataTableCell>
						})}
					</DataTableRow>
				</DataTableHead>
				<DataTableBody>
					{customers.map((customer) => (
						<CustomerRow
							key={customer.id}
							customer={customer}
							includedColumns={includedColumns}
							include={include}
							onSelect={onSelect}
						/>
					))}
				</DataTableBody>
			</DataTable>
			<PagingNavBar
				hasResults={hasResults}
				hasPrev={hasPrev}
				hasNext={hasNext}
				prev={prev}
				next={next}
				isShow={isUsingPaging}
				meta={meta}
			/>
		</div>
	)
}
